import type { ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useT } from "../i18n";
import { Marke, Punkt, euro } from "../bausteine";
import { Zeichen } from "../bausteine/Zeichen";
import type { StartAusblick } from "../daten/typen";

/* ══════════════════════════════════════════════════════════════════
   Drei Karten neben den Fallspalten (Luca, 08.10.2026).

   Erst gebaut als eigene zweite Reihe: Qualifikationen · Prüfungen &
   Werkstatt · Umsatz. Am selben Abend neu geschnitten:

   | oben  | Meine Aufgaben | Zu verteilen | **Was geht heute raus** |
   | unten | **Zu entscheiden** | **Fristen & Werkstatt** | Umsatz |

   Luca: *„dass ablaufende Qualifikationen und Prüfungen in der Werkstatt
   zusammenlaufen, sodass da alles, was abläuft, was steht, rein kommt"* —
   und *„Was geht heute raus, mit dem Zusatz, ob das eine Abholung ist von
   einem Kunden oder ob wir das rausbringen."*

   **Fristen sind Vorschau, keine Aufgabe.** Bis hierher stand
   „Kranführerschein läuft ab" unter „Meine Aufgaben" — ein Schein in sechs
   Wochen ist niemandes Handgriff von heute. Die Fallspalten lassen
   Qualifikation und Prüftermin deshalb aus (`masken/Heute.tsx`); nur ein
   überfälliges Gerät beim Kunden bleibt dort rot stehen. Die Daten:
   `GET /start` → `ausblick` (`dienste/ausblick.ts`) und `ausgaenge`
   (`dienste/tagesbewegungen.ts`, dieselbe Liste wie im Kalender).
   ══════════════════════════════════════════════════════════════════ */

/** „in 5 Tagen", „heute", „seit 3 Tagen" — dieselben Wörter wie die Fristen. */
function wann(tage: number, t: ReturnType<typeof useT>): string {
  if (tage < 0) return t("start.ausblick.seitTagen", { zahl: -tage });
  if (tage === 0) return t("start.ausblick.heute");
  return t("start.ausblick.inTagen", { zahl: tage });
}

/** Rot nur, was überfällig beim Kunden steht; Ring, was vorbei oder binnen 7 Tagen ist. */
function stufe(tage: number, draussen = false): "stopp" | "acht" | null {
  if (tage < 0) return draussen ? "stopp" : "acht";
  return tage <= 7 ? "acht" : null;
}

function Zeile({ titel, unten, tage, draussen, weg }: {
  titel: string; unten: string; tage: number; draussen?: boolean; weg: () => void;
}) {
  const t = useT();
  const s = stufe(tage, draussen);
  return (
    <button type="button" className="ausblick-zeile" onClick={weg}>
      <span className="ausblick-punkt">{s ? <Punkt art={s} /> : null}</span>
      <span className="ausblick-text">
        <b>{titel}</b>
        <span>{unten}</span>
      </span>
      <span className={`ausblick-wann${tage < 0 ? " vorbei" : ""}`}>{wann(tage, t)}</span>
    </button>
  );
}

function Karte({ titel, zahl, leer, children }: {
  titel: string; zahl: number; leer: string; children: ReactNode;
}) {
  return (
    <div className="karte start-karte ausblick">
      <div className="karte-kopf">
        <h4>{titel}</h4>
        {zahl ? <Marke>{zahl}</Marke> : null}
      </div>
      <div className="start-karte-leib">
        {zahl ? children : <p className="fall-leer">{leer}</p>}
      </div>
    </div>
  );
}

/** **Was geht heute raus** — mit Lieferung oder „Kunde holt" je Zeile. */
export function HeuteRausKarte({ raus }: { raus: StartAusblick["raus"] }) {
  const t = useT();
  const gehZu = useNavigate();
  return (
    <div className="karte start-karte ausblick">
      <div className="karte-kopf">
        <h4>{t("start.ausblick.rausTitel")}</h4>
        {raus.length ? <Marke>{raus.length}</Marke> : null}
        <button type="button" className="fall-kopf-weg" onClick={() => void gehZu({ to: "/kalender" })}>
          {t("start.ausblick.zumKalender")} ›
        </button>
      </div>
      <div className="start-karte-leib">
        {raus.length ? raus.map((z) => (
          <button
            type="button" key={z.id} className="ausblick-zeile raus"
            onClick={() => (z.vorgang
              ? void gehZu({ to: "/auftraege/$nr", params: { nr: z.vorgang } })
              : void gehZu({ to: "/kalender" }))}
          >
            <span className="ausblick-punkt">
              {z.verspaetetSeit ? <Punkt art="acht" /> : z.erledigt ? <Punkt art="gut" /> : null}
            </span>
            <span className="ausblick-text">
              <b>{[z.kennung, z.kunde].filter(Boolean).join(" · ")}</b>
              <span>{[z.ort, z.vorgang].filter(Boolean).join(" · ") || "\u00a0"}</span>
            </span>
            <span className="ausblick-rechts">
              {/* Fahren wir, oder kommt der Kunde? Dieselbe Marke wie im Kalender. */}
              {z.uebergabeart ? (
                <span
                  className={`marke tz-weg${z.uebergabeart === "liefern" ? " wir" : ""}`}
                  title={t(`planung.weg.raus.${z.uebergabeart}Satz`)}
                >
                  <Zeichen name={z.uebergabeart === "liefern" ? "lkw" : "kunden"} />
                  {t(`planung.weg.raus.${z.uebergabeart}`)}
                </span>
              ) : null}
              <span className={`ausblick-wann${z.verspaetetSeit ? " vorbei" : ""}`}>
                {z.erledigt
                  ? t("planung.ueberblickErledigt")
                  : z.verspaetetSeit
                    ? t("start.ausblick.verspaetetSeit", { datum: datumKurz(z.verspaetetSeit) })
                    : z.uhr}
              </span>
            </span>
          </button>
        )) : <p className="fall-leer">{t("planung.ueberblickRausLeer")}</p>}
      </div>
    </div>
  );
}

/**
 * **Fristen & Werkstatt** — Qualifikationen, UVV, Prüfung, Wartung und
 * Werkstatt in einer Liste, nach Tagen sortiert: was am nächsten ist, oben.
 */
export function FristenKarte({ ausblick }: { ausblick: StartAusblick }) {
  const t = useT();
  const gehZu = useNavigate();
  const zuRessourcen = () => void gehZu({ to: "/ressourcen" });
  const zeilen = [
    ...ausblick.qualifikationen.map((q) => ({
      schluessel: `q-${q.person}-${q.wort}-${q.bis}`, tage: q.tage, draussen: false,
      titel: t(q.tage < 0 ? "start.ausblick.qualiAbgelaufen" : "start.ausblick.qualiLaeuftAb", {
        wort: q.wort, datum: datumKurz(q.bis),
      }),
      unten: q.person,
    })),
    ...ausblick.termine.map((x) => ({
      schluessel: `t-${x.art}-${x.kennung}-${x.am}`, tage: x.tage, draussen: x.draussen,
      titel: `${x.kennung} · ${t(`start.ausblick.art.${x.art}`)}`,
      unten: [x.typ, x.notiz, x.draussen ? t("start.ausblick.draussen") : null, datumKurz(x.am)]
        .filter(Boolean).join(" · "),
    })),
  ].sort((a, b) => a.tage - b.tage);

  return (
    <Karte titel={t("start.ausblick.fristenTitel")} zahl={zeilen.length} leer={t("start.ausblick.fristenLeer")}>
      {zeilen.map((z) => (
        <Zeile key={z.schluessel} titel={z.titel} unten={z.unten} tage={z.tage} draussen={z.draussen} weg={zuRessourcen} />
      ))}
    </Karte>
  );
}

/** Der Umsatz als Karte — `null`, wenn der Anrufer keine Beträge sehen darf. */
export function UmsatzAusblick({ ausblick, monatJetzt, vormonat, umsatzBelege }: {
  ausblick: StartAusblick; monatJetzt: string; vormonat: string; umsatzBelege: number | null;
}) {
  const gehZu = useNavigate();
  if (!ausblick.umsatz) return null;
  return (
    <UmsatzKarte
      monate={ausblick.umsatz} monatJetzt={monatJetzt} vormonat={vormonat} belege={umsatzBelege ?? 0}
      weg={() => void gehZu({ to: "/abrechnung" })}
    />
  );
}

/** `2026-10-22` → `22.10.` */
function datumKurz(tag: string): string {
  const [, m, d] = tag.split("-");
  return d && m ? `${d}.${m}.` : tag;
}

/* ══ Der Umsatz als Balken ══
   Eine Reihe, ein Wert je Monat — also ein Balkendiagramm mit einer Farbe
   und ohne Legende; der Titel sagt, was es ist. Der laufende Monat trägt
   Stahlblau, die fünf davor dieselbe Farbe gedämpft. Die Zahl des Monats
   steht groß darüber, denn die wird gefragt — der Verlauf ist die zweite
   Auskunft. Jeder Balken trägt seinen Betrag im `title`, und für den
   Vorleser steht dasselbe als Tabelle darunter. */
function UmsatzKarte({ monate, monatJetzt, vormonat, belege, weg }: {
  monate: { monat: string; netto: number }[];
  monatJetzt: string;
  vormonat: string;
  belege: number;
  weg: () => void;
}) {
  const t = useT();
  const namen = t.liste("kalender.monateKurz");
  const jetzt = monate[monate.length - 1];
  const davor = monate[monate.length - 2];
  const hoechst = Math.max(...monate.map((m) => m.netto), 0);
  // `2026-10` → „Okt": der Monat ist die zweite Hälfte des Schlüssels aus der API.
  const name = (monat: string) => namen[Number(monat.split("-")[1] ?? 0) - 1] ?? monat;

  return (
    <div className="karte start-karte ausblick umsatz">
      <div className="karte-kopf">
        <h4>{t("start.umsatz", { monat: monatJetzt })}</h4>
        <button type="button" className="fall-kopf-weg" onClick={weg}>{t("start.ausblick.zurAbrechnung")} ›</button>
      </div>
      <div className="umsatz-leib">
        <p className={`umsatz-gross${belege ? "" : " leise"}`}>
          {euro(jetzt?.netto ?? 0)}<i>{t("start.umsatzWaehrung")}</i>
        </p>
        <p className="umsatz-unten">
          {belege
            ? t(belege === 1 ? "start.umsatzUntenEins" : "start.umsatzUnten", { zahl: belege })
            : t("start.umsatzLeer")}
          {" · "}
          {t("start.umsatzVergleich", { monat: vormonat })}{" "}
          <span className="umsatz-betrag">{euro(davor?.netto ?? 0)} {t("start.umsatzWaehrung")}</span>
        </p>
        <div className="umsatz-balken" aria-hidden="true">
          {monate.map((m, i) => {
            const hoehe = hoechst > 0 ? Math.max((m.netto / hoechst) * 100, m.netto > 0 ? 2 : 0) : 0;
            const letzter = i === monate.length - 1;
            return (
              <div key={m.monat} className="umsatz-spalte" title={`${name(m.monat)}: ${euro(m.netto)} ${t("start.umsatzWaehrung")}`}>
                <div className="umsatz-saeule">
                  <i className={letzter ? "jetzt" : ""} style={{ height: `${hoehe}%` }} />
                </div>
                <span className={letzter ? "jetzt" : ""}>{name(m.monat)}</span>
              </div>
            );
          })}
        </div>
        {hoechst === 0 ? <p className="umsatz-hinweis">{t("start.ausblick.umsatzNoch")}</p> : null}
        <table className="nur-vorleser">
          <caption>{t("start.ausblick.umsatzTabelle")}</caption>
          <tbody>
            {monate.map((m) => (
              <tr key={m.monat}><th scope="row">{name(m.monat)}</th><td>{euro(m.netto)} {t("start.umsatzWaehrung")}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
